import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import NormalButton from '../Button/NormalButton';
import WhiteRoundButton from '../Button/WhiteRoundButton';
import ColorRoundButton from '../Button/ColorRoundButton';
import { I_EDIT, I_TRASH } from '../../types/images';
import { isKioskCommunicationCategory, parseKioskContent } from '../../lib/communicationBox';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';

type Props = {
    modalObject: any;
    isInputMode: boolean;
    isEditMode: boolean;
    text: string;
    editText: string;
    onClickCreateReply: () => void;
    onClickReplyButton: () => void;
    onClickEditReply: () => void;
    onClickEditReplyButton: () => void;
    onClickDeleteReply: () => void;
};
const ModalDetailCommunicationBottom = ({
    modalObject,
    isInputMode,
    isEditMode,
    text,
    editText,
    onClickCreateReply,
    onClickReplyButton,
    onClickEditReply,
    onClickEditReplyButton,
    onClickDeleteReply,
}: Props) => {
    const isKiosk = isKioskCommunicationCategory(modalObject?.category);
    const parsedKiosk = isKiosk ? parseKioskContent(modalObject?.contents?.[0]?.content) : null;
    const isActionOnlyType =
        parsedKiosk?.type === 'visitor_memo' || parsedKiosk?.type === 'trainer_promo_lead';

    const navigate = useNavigate();
    const { isNewOpportunityEnabled } = useCenterFeatureFlags();
    const [copiedPhone, setCopiedPhone] = useState(false);

    if (isActionOnlyType) {
        const isVisitorMemo = parsedKiosk?.type === 'visitor_memo';
        const phone = parsedKiosk?.phone;

        const handleCopyPhone = async () => {
            if (!phone) return;
            try {
                await navigator.clipboard.writeText(phone);
                setCopiedPhone(true);
                setTimeout(() => setCopiedPhone(false), 1800);
            } catch {
                /* noop */
            }
        };

        return (
            <Col
                style={{
                    width: 588,
                    alignSelf: 'center',
                    marginTop: 24,
                    padding: '16px 18px',
                    borderRadius: 12,
                    backgroundColor: isVisitorMemo ? '#EFF6FF' : '#ECFDF5',
                    border: `1px solid ${isVisitorMemo ? '#BFDBFE' : '#A7F3D0'}`,
                    gap: 8,
                }}
            >
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>📞</span>
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isVisitorMemo ? '#1E3A8A' : '#065F46',
                        }}
                    >
                        {isVisitorMemo
                            ? '회원에게 직접 연락해 후속 안내해주세요'
                            : '회원에게 연락 후 강사 매칭 · 일정 안내를 진행해주세요'}
                    </span>
                </Row>
                <span
                    style={{
                        fontSize: 12,
                        color: isVisitorMemo ? '#1E40AF' : '#047857',
                        lineHeight: 1.65,
                    }}
                >
                    {isVisitorMemo
                        ? '방문자가 남긴 정보성 메모예요. 전화나 방문 응대로 처리하고, '
                        : '강사 홍보판에서 들어온 신청이에요. 회원에게 직접 연락드린 뒤 해당 강사 일정·상담을 안내하고, '}
                    <b>"신규기회"로 연결</b>해주세요.
                </span>
                {phone && (
                    <Row style={{ alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#6B7280' }}>연락처</span>
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: isVisitorMemo ? '#1D4ED8' : '#047857',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                backgroundColor: 'white',
                                border: `1px solid ${isVisitorMemo ? '#BFDBFE' : '#A7F3D0'}`,
                                padding: '4px 10px',
                                borderRadius: 6,
                            }}
                        >
                            {phone}
                        </span>
                        <button
                            type="button"
                            onClick={handleCopyPhone}
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: copiedPhone ? '#166534' : (isVisitorMemo ? '#1D4ED8' : '#047857'),
                                backgroundColor: copiedPhone ? '#DCFCE7' : 'white',
                                border: `1px solid ${copiedPhone ? '#86EFAC' : (isVisitorMemo ? '#BFDBFE' : '#A7F3D0')}`,
                                padding: '4px 10px',
                                borderRadius: 6,
                                cursor: 'pointer',
                            }}
                        >
                            {copiedPhone ? '복사됨 ✓' : '복사'}
                        </button>
                    </Row>
                )}
                {isNewOpportunityEnabled && (
                    <Row style={{ marginTop: 6 }}>
                        <button
                            type="button"
                            onClick={() => navigate('/centerReport/newOpportunities')}
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: 'white',
                                backgroundColor: isVisitorMemo ? '#2563EB' : '#059669',
                                border: 'none',
                                padding: '8px 14px',
                                borderRadius: 8,
                                cursor: 'pointer',
                            }}
                        >
                            신규기회 페이지로 이동 →
                        </button>
                    </Row>
                )}
            </Col>
        );
    }

    return (
        <>
            {modalObject?.status === 'PENDING' ? (
                <Row
                    style={{
                        width: '100%',
                        justifyContent: 'flex-end',
                        marginTop: 36,
                        paddingRight: 72,
                    }}
                >
                    {isInputMode ? (
                        <ColorRoundButton
                            isDisabled={text.trim().length === 0}
                            size="small"
                            callBack={onClickCreateReply}
                            text={'답변 작성'}
                        />
                    ) : (
                        <WhiteRoundButton
                            text="답변 작성"
                            callBack={onClickReplyButton}
                            size="small"
                            customStyle={{ width: 111, height: 36 }}
                        />
                    )}
                </Row>
            ) : isEditMode ? (
                <Row
                    style={{
                        width: '100%',
                        justifyContent: 'flex-end',
                        marginTop: 36,
                        paddingRight: 72,
                    }}
                >
                    <ColorRoundButton
                        isDisabled={editText.trim().length === 0}
                        size="small"
                        callBack={onClickEditReply}
                        text={'답변 보내기'}
                    />
                </Row>
            ) : (
                <Row
                    style={{
                        width: 588,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        alignSelf: 'center',
                        marginTop: 12,
                    }}
                >
                    <Row
                        onClick={onClickDeleteReply}
                        style={{
                            width: 32,
                            height: 32,
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <img src={I_TRASH} style={{ width: 24, height: 24 }} />
                    </Row>
                    <Row
                        onClick={onClickEditReplyButton}
                        style={{
                            width: 32,
                            height: 32,
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <img src={I_EDIT} style={{ width: 24, height: 24 }} />
                    </Row>
                    {/* <NormalButton onClick={onClickEditReplyButton} text={'내용 수정'} style={{ marginRight: 24 }} />
                    <NormalButton onClick={onClickDeleteReply} text={'답변 삭제'} /> */}
                </Row>
            )}
        </>
    );
};

export default ModalDetailCommunicationBottom;
